/**
 * Workspace Tabs Component
 * Tab navigation for project workspace with count badges
 */

import { Link } from 'react-router-dom';
import { Map, AlertTriangle, FileText, EyeOff } from 'lucide-react';

interface TabCounts {
  elements?: number;
  photos?: number;
  anomalies?: number;
  notInspected?: number;
}

interface WorkspaceTabsProps {
  projectId: string;
  activeTab: string;
  counts?: TabCounts;
}

const tabs = [
  { id: 'map', label: 'Map', icon: Map, path: 'map', countKey: 'elements' as const },
  { id: 'anomalies', label: 'Anomalies', icon: AlertTriangle, path: 'anomalies', countKey: 'anomalies' as const },
  { id: 'not-inspected', label: 'Not Inspected', icon: EyeOff, path: 'not-inspected', countKey: 'notInspected' as const },
  { id: 'report', label: 'Report', icon: FileText, path: 'report', countKey: null },
];

export function WorkspaceTabs({ projectId, activeTab, counts }: WorkspaceTabsProps) {
  return (
    <div className="bg-white/80 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700/50 px-6 transition-colors">
      <nav className="flex gap-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const count = tab.countKey && counts ? counts[tab.countKey] : undefined;
          
          return (
            <Link
              key={tab.id}
              to={`/projects/${projectId}/${tab.path}`}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-mono font-medium border-b-2 transition-colors
                ${isActive 
                  ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400' 
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-500'
                }
              `}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
              {count !== undefined && count > 0 && (
                <span 
                  className={`
                    min-w-[20px] h-5 px-1.5 rounded text-xs font-mono font-medium flex items-center justify-center
                    ${isActive 
                      ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30' 
                      : 'bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-600/50'
                    }
                    ${tab.id === 'anomalies' && count > 0 
                      ? 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/30'
                      : ''
                    }
                  `}
                >
                  {count > 999 ? '999+' : count}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
